import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  FileCheck, 
  CheckCircle, 
  BarChart3, 
  Shield, 
  ArrowRight,
  Calculator,
  History,
  GitCompare
} from 'lucide-react';

export default function LandingPage() {
  const isNative = window.Capacitor?.isNativePlatform?.() === true;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-forest rounded-sm flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-bold text-lg tracking-tight text-forest">BustaControllo</span>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                className="inline-flex"
                onClick={() => { window.location.href = '/login'; }}
                data-testid="login-nav"
              >Accedi</Button>
              <Button asChild className="swiss-btn-primary">
                <Link to="/register" data-testid="register-nav">Inizia Gratis</Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-mint to-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Controlla le tue
              <span className="text-forest"> buste paga</span>
              <br />in pochi click
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Verifica automaticamente i calcoli di IRPEF, INPS e netto. 
              Individua errori e anomalie nelle tue retribuzioni mensili.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="swiss-btn-primary h-14 px-8 text-base">
                <Link to="/register" data-testid="hero-cta">
                  Inizia Gratuitamente
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="swiss-btn-secondary h-14 px-8 text-base">
                <Link to="/login">Ho già un account</Link>
              </Button>
            </div>

            {!isNative && (
              <div className="mt-8 flex flex-col items-center gap-3">
                <p className="text-sm text-muted-foreground">Disponibile su</p>
                <div className="flex flex-row gap-3 items-center justify-center flex-wrap">
                  <a
                    href="https://apps.apple.com/it/app/bustacontrollo/id6769530129"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-opacity hover:opacity-80"
                    aria-label="Scarica BustaControllo su App Store"
                  >
                    <img
                      src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                      alt="Scarica su App Store"
                      className="h-10"
                    />
                  </a>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto">
              <div>
                <div className="font-mono text-3xl md:text-4xl font-bold text-forest">9.19%</div>
                <div className="text-sm text-muted-foreground mt-1">INPS Verifica</div>
              </div>
              <div>
                <div className="font-mono text-3xl md:text-4xl font-bold text-forest">IRPEF</div>
                <div className="text-sm text-muted-foreground mt-1">Aliquote 2024</div>
              </div>
              <div>
                <div className="font-mono text-3xl md:text-4xl font-bold text-forest">6.91%</div>
                <div className="text-sm text-muted-foreground mt-1">TFR Controllo</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Tutto sotto controllo
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Un sistema completo per verificare ogni aspetto della tua busta paga
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="p-8 border border-slate-200 rounded-sm hover:border-forest/30 transition-colors">
              <div className="w-12 h-12 bg-mint rounded-sm flex items-center justify-center mb-6">
                <Calculator className="w-6 h-6 text-forest" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Verifica Automatica</h3>
              <p className="text-muted-foreground">
                Controlla automaticamente tutti i calcoli: lordo, netto, IRPEF, INPS e addizionali regionali e comunali.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="p-8 border border-slate-200 rounded-sm hover:border-forest/30 transition-colors">
              <div className="w-12 h-12 bg-mint rounded-sm flex items-center justify-center mb-6">
                <CheckCircle className="w-6 h-6 text-forest" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Rileva Anomalie</h3>
              <p className="text-muted-foreground">
                Identifica immediatamente errori e discrepanze nei calcoli della tua busta paga con report dettagliati.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="p-8 border border-slate-200 rounded-sm hover:border-forest/30 transition-colors">
              <div className="w-12 h-12 bg-mint rounded-sm flex items-center justify-center mb-6">
                <History className="w-6 h-6 text-forest" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Storico Completo</h3>
              <p className="text-muted-foreground">
                Conserva tutte le tue buste paga in un archivio sicuro. Accedi ai dati storici in qualsiasi momento.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="p-8 border border-slate-200 rounded-sm hover:border-forest/30 transition-colors">
              <div className="w-12 h-12 bg-mint rounded-sm flex items-center justify-center mb-6">
                <GitCompare className="w-6 h-6 text-forest" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Confronta Periodi</h3>
              <p className="text-muted-foreground">
                Confronta le buste paga di mesi diversi per analizzare l'andamento della tua retribuzione nel tempo.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="p-8 border border-slate-200 rounded-sm hover:border-forest/30 transition-colors">
              <div className="w-12 h-12 bg-mint rounded-sm flex items-center justify-center mb-6">
                <BarChart3 className="w-6 h-6 text-forest" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Dashboard Interattiva</h3>
              <p className="text-muted-foreground">
                Visualizza statistiche e grafici per comprendere meglio l'andamento delle tue retribuzioni.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="p-8 border border-slate-200 rounded-sm hover:border-forest/30 transition-colors">
              <div className="w-12 h-12 bg-mint rounded-sm flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-forest" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Dati Protetti</h3>
              <p className="text-muted-foreground">
                I tuoi dati sono al sicuro. Utilizziamo crittografia avanzata per proteggere tutte le informazioni.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Come funziona
            </h2>
            <p className="text-lg text-muted-foreground">
              Tre semplici passaggi per verificare la tua busta paga
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-forest text-white rounded-full flex items-center justify-center mx-auto mb-6 font-mono text-2xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-semibold mb-3">Inserisci i dati</h3>
                <p className="text-muted-foreground">
                  Compila il form con i dati della tua busta paga: lordo, trattenute e netto.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-forest text-white rounded-full flex items-center justify-center mx-auto mb-6 font-mono text-2xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-semibold mb-3">Verifica automatica</h3>
                <p className="text-muted-foreground">
                  Il sistema verifica tutti i calcoli secondo la normativa italiana vigente.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-forest text-white rounded-full flex items-center justify-center mx-auto mb-6 font-mono text-2xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-semibold mb-3">Ricevi il report</h3>
                <p className="text-muted-foreground">
                  Ottieni un report dettagliato con eventuali anomalie e suggerimenti.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-forest text-white">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
            Inizia a controllare le tue buste paga
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Registrati gratuitamente e inizia subito a verificare che i tuoi stipendi siano calcolati correttamente.
          </p>
          <Button asChild className="bg-white text-forest hover:bg-white/90 h-14 px-8 text-base rounded-none font-medium">
            <Link to="/register" data-testid="footer-cta">
              Crea Account Gratuito
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 pb-24 border-t border-slate-200">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-forest rounded-sm flex items-center justify-center">
                <FileCheck className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-bold tracking-tight text-forest">BustaControllo</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://offlinepdfstudio.com/it/" className="text-sm text-forest hover:underline">
                Strumenti PDF gratis e offline
              </a>
              <Link to="/controllo-sindacale" className="text-sm text-forest hover:underline" data-testid="footer-controllo-sindacale">
                Controllo gratuito busta paga
              </Link>
              <p className="text-sm text-muted-foreground">
                © 2024 BustaControllo. Tutti i diritti riservati.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
